import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PromotionGroupInterface } from '../../data-access/models/promotion-group.interface';
import { PromotionApiService } from '../../data-access/services/promotion-api.service';
import { PromotionGroupTypeEnum } from '../../data-access/models/promotion-group-type.enum';
import { HorizontalScrollComponent, TitleSummaryComponent } from '@client-monorepo/common/ui-components';
import { PromotionItemInterface } from '../../data-access/models/promotion-item.interface';
import { getHostname, rangeCreator } from '@client-monorepo/common/utilities';
import { ProductInterface, ProductPreviewComponent } from '@client-monorepo/daily-fintech/stores';
import { NgxSkeletonLoadingComponent } from '@digipay/ngx-skeleton-loading';
import { interval, map, Observable, startWith, Subscription, switchMap, take } from 'rxjs';
import { OrderTypes, RestrictionTypes, SearchPayloadInterface } from '@client-monorepo/common/network';
import { PromotionItemRestrictionEnum } from '../../data-access/models/promotion-item-restriction.enum';
import { PromotionItemsSearchResponseInterface } from '../../data-access/models/promotion-items-search-response.interface';
import { RouterLink } from '@angular/router';
import { ActionHandlerService, ActionType, RedirectionTypeEnum } from '@client-monorepo/common/action-handler';
import { AnalyticsEventType, EventManagementService } from '@client-monorepo/common/event-management';
import { NgxButtonComponent } from '@digipay/ngx-button';
import { ApiImageModule } from '@digipay/ng-ui-api-image';
import { NgxDpCarouselComponent, NgxDpCarouselSlideDirective } from '@digipay/ngx-dp-carousel';

@Component({
  selector: 'common-promotions-promotion-carousel',
  standalone: true,
  imports: [
    CommonModule,
    TitleSummaryComponent,
    HorizontalScrollComponent,
    ProductPreviewComponent,
    NgxSkeletonLoadingComponent,
    RouterLink,
    NgxButtonComponent,
    ApiImageModule,
    NgxDpCarouselComponent,
    NgxDpCarouselSlideDirective,
  ],
  templateUrl: './promotion-carousel.component.html',
  styleUrl: './promotion-carousel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.d-none]': '!isLoading() && !promotionItems().length' },
})
export class PromotionCarouselComponent implements OnInit, OnDestroy {
  isLoading = signal(true);
  promotionGroupId = input.required<string>();
  promotionGroup = signal<PromotionGroupInterface | undefined>(undefined);
  promotionItems = signal<Array<PromotionItemInterface>>([]);
  promotionApiService = inject(PromotionApiService);
  PromotionGroupTypeEnum = PromotionGroupTypeEnum;
  timerSubscription!: Subscription;
  targetTime = computed(() => {
    const items = this.promotionItems().sort((a, b) => {
      return a.endTime - b.endTime;
    });
    return items[0].endTime ? Math.round(items[0].endTime / 1000) : 0;
  });
  time = signal<Array<string>>(['00', '00', '00']);
  rangeCreator = rangeCreator;
  type = computed(() => {
    return this.promotionGroup()?.type ?? PromotionGroupTypeEnum.NORMAL;
  });
  actionHandler = inject(ActionHandlerService);
  eventManagement = inject(EventManagementService);
  nothingToShow = output<boolean>();
  animationIsRunningVariable = signal(false);

  constructor() {
    effect(() => {
      if (!this.isLoading() && !this.promotionItems().length) {
        this.nothingToShow.emit(true);
      } else {
        this.nothingToShow.emit(false);
      }
    });
  }

  ngOnInit(): void {
    if (this.promotionGroupId()) {
      this.loadPromotionItems();
    }
  }

  ngOnDestroy(): void {
    this.timerSubscription?.unsubscribe();
  }

  loadPromotionItems(): void {
    this.isLoading.set(true);
    this.timerSubscription?.unsubscribe();
    this.promotionItems.set([]);
    this.promotionGroup.set(undefined);
    if (this.promotionGroupId()) {
      this.promotionApiService
        .getPromotionGroup(this.promotionGroupId())
        .pipe(
          switchMap((promotionGroup) => {
            if (promotionGroup) {
              this.promotionGroup.set(promotionGroup);
              const payload: SearchPayloadInterface<PromotionItemRestrictionEnum> = {
                page: 0,
                size: 10,
                orders: [
                  {
                    field: 'order',
                    order: OrderTypes.ASC,
                  },
                ],
                restrictions: [
                  {
                    type: RestrictionTypes.SIMPLE,
                    field: PromotionItemRestrictionEnum.GROUPID,
                    operation: 'eq',
                    value: this.promotionGroupId(),
                  },
                ],
              };
              return this.promotionApiService.getPromotionItemsList(payload);
            } else {
              return new Observable<null>((observer) => {
                observer.next(null);
                observer.complete();
              });
            }
          }),
        )
        .subscribe({
          next: (promotionsItems: PromotionItemsSearchResponseInterface | null) => {
            if (promotionsItems && Array.isArray(promotionsItems.items) && promotionsItems.items.length) {
              this.promotionItems.set(promotionsItems.items);
              this.initComponent();
            } else {
              this.isLoading.set(false);
            }
          },
        });
    }
  }

  initComponent(): void {
    this.promotionItems.update(() => {
      return this.promotionItems().sort((first: PromotionItemInterface, second: PromotionItemInterface) => {
        return first.endTime - second.endTime;
      });
    });
    const now = Math.floor(Date.now() / 1000);
    this.timerSubscription = this.createCountdown(this.targetTime() - now).subscribe({
      next: (time) => {
        if (Array.isArray(time)) {
          this.time.set(time);
        }
      },
      complete: () => {
        this.timerSubscription?.unsubscribe();
        this.loadPromotionItems();
      },
    });
    this.isLoading.set(false);
  }

  createCountdown(seconds: number) {
    return interval(1000).pipe(
      // Convert each tick into the remaining time
      map((count) => this.formatTime(seconds - count - 1)),
      // Start with the initial countdown value
      startWith(seconds),
      // Complete the countdown when it reaches zero
      take(seconds + 1),
    );
  }

  formatTime(totalSeconds: number): string[] {
    let hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    hours = Math.min(hours, 99);
    return [`${String(hours).padStart(2, '0')}`, `${String(minutes).padStart(2, '0')}`, `${String(seconds).padStart(2, '0')}`];
  }

  clickOnItem(product: ProductInterface): void {
    if (this.animationIsRunningVariable()) {
      return;
    }
    this.eventManagement.triggerEventImmediately({
      type: AnalyticsEventType.REDIRECT,
      to: product.url,
      meta: {
        host: getHostname(product.url),
      },
      breadCrumbs: ['promotion-carousel', 'product'],
    });
    this.actionHandler.handle({
      type: ActionType.REDIRECT,
      payload: {
        type: RedirectionTypeEnum.blank,
        url: product.url,
        params: {
          'dp-source': 'DP',
          'dp-medium': this.type() === PromotionGroupTypeEnum.NORMAL ? 'normal-carousel' : 'amazing-carousel',
          'dp-type': 'product',
          'dp-campaign': this.promotionGroup()?.title || '',
        },
      },
    });
  }

  animationIsRunning(isRunning: boolean) {
    if (!isRunning) {
      setTimeout(() => {
        this.animationIsRunningVariable.set(false);
      }, 200);
    } else {
      this.animationIsRunningVariable.set(true);
    }
  }
}
